import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { t } from 'i18next';
import { useState } from 'react';

import Dialog from '~components/Dialog/Dialog';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { PseudoLink } from '~components/Helpers/PseudoLink';

// The roles a company admin can assign. Platform Admin, Impersonator, Auditor, and
// Billing Portal are Tread-staff roles the API hides from customers.
const ASSIGNABLE_ROLE_KEYS = [
  'company_admin',
  'dispatcher',
  'biller',
  'manager',
  'foreman',
  'driver',
  'salesperson',
  'ticket_approver',
  'viewer',
  'reporting',
  'it_admin',
  'data_importer',
] as const;

const COLUMN_KEYS = ['purpose', 'use_when', 'restrictions'] as const;

const INTRO_KEYS = ['additive', 'view_rate', 'hide_rates', 'role_changes'] as const;

const RoleGuide = () => (
  <Box data-test-id="company-onboarding-role-guide">
    <Box
      component="ul"
      sx={{ color: 'text.secondary', fontSize: 13, mb: 1, mt: 0, pl: 2.5 }}
    >
      {INTRO_KEYS.map((introKey) => (
        <li key={introKey}>{t(`company_onboarding.roles.intro_${introKey}`)}</li>
      ))}
    </Box>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{t('company_onboarding.roles.column_role')}</TableCell>
          {COLUMN_KEYS.map((columnKey) => (
            <TableCell key={columnKey}>
              {t(`company_onboarding.roles.column_${columnKey}`)}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {ASSIGNABLE_ROLE_KEYS.map((roleKey) => (
          <TableRow data-test-id={`company-onboarding-role-${roleKey}`} key={roleKey}>
            <TableCell
              sx={{ fontWeight: 600, verticalAlign: 'top', whiteSpace: 'nowrap' }}
            >
              {t(`company_onboarding.roles.rows.${roleKey}.name`)}
            </TableCell>
            {COLUMN_KEYS.map((columnKey) => (
              <TableCell key={columnKey} sx={{ fontSize: 13, verticalAlign: 'top' }}>
                {t(`company_onboarding.roles.rows.${roleKey}.${columnKey}`)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

const RoleDefinitionsLink = () => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <PseudoLink
        action={() => setIsOpen(true)}
        dataTestId="company-onboarding-role-definitions-link"
        linkColor={theme.semantic.linkText}
        sx={{ mt: 1 }}
        title={t('company_onboarding.roles.view_definitions')}
      />
      <Dialog isOpen={isOpen} maxWidth="lg" onClose={() => setIsOpen(false)}>
        <DialogHeader onClose={() => setIsOpen(false)}>
          {t('company_onboarding.roles.heading')}
        </DialogHeader>
        <DialogContent>
          <RoleGuide />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RoleDefinitionsLink;
